import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import {
  CprngEndpointAccessTier,
  CprngEndpointRow,
} from '../../../services/component-data-source/data/organizations/cprng-endpoints';

const TIER_LABELS: Record<CprngEndpointAccessTier, string> = {
  public: 'Offentlig',
  everyone: 'Everyone (router)',
  authenticated_roles: 'Roller',
  misconfigured: 'Misconfigured',
};

@Component({
  selector: 'app-duna-cprng-endpoints',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatPaginatorModule,
  ],
  templateUrl: './duna-cprng-endpoints.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './duna-cprng-endpoints.component.scss',
})
export class DunaCprngEndpointsComponent implements OnInit, AfterViewInit {
  readonly pageSize = 25;
  readonly pageSizeOptions = [25];

  @ViewChild(MatPaginator) private paginator?: MatPaginator;

  readonly tierLabels = TIER_LABELS;
  readonly tierOptions: CprngEndpointAccessTier[] = [
    'public',
    'everyone',
    'authenticated_roles',
    'misconfigured',
  ];

  loading = false;
  errorMessage: string | null = null;
  search = '';
  tierFilter: CprngEndpointAccessTier | 'all' = 'all';

  private allRows: CprngEndpointRow[] = [];
  tableDataSource = new MatTableDataSource<CprngEndpointRow>([]);
  displayedColumns = [
    'method',
    'path',
    'access_tier',
    'roles',
    'groups',
    'requires_login',
    'handler',
  ];

  misconfiguredCount = 0;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.connectPaginator();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.http
      .get<{ items: CprngEndpointRow[] }>(
        `${environment.apiUrl}/cprng/endpoints`,
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.allRows = res.items ?? [];
          this.misconfiguredCount = this.allRows.filter(
            (r) => r.access_tier === 'misconfigured'
          ).length;
          this.applyFilters();
        },
        error: () => {
          this.errorMessage =
            'Kunne ikke hente endpoint-liste (kræver superadmin).';
        },
      });
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  setTierFilter(tier: CprngEndpointAccessTier | 'all'): void {
    this.tierFilter = tier;
    this.applyFilters();
  }

  tierClass(tier: CprngEndpointAccessTier): string {
    return `tier-${tier}`;
  }

  /** Safe for mat-table rows (template infers `row` as implicit any). */
  tierLabel(tier: CprngEndpointAccessTier | string): string {
    return TIER_LABELS[tier as CprngEndpointAccessTier] ?? String(tier);
  }

  private applyFilters(): void {
    const q = this.search.trim().toLowerCase();
    let rows = this.allRows;
    if (this.tierFilter !== 'all') {
      rows = rows.filter((r) => r.access_tier === this.tierFilter);
    }
    if (q) {
      rows = rows.filter(
        (r) =>
          r.path.toLowerCase().includes(q) ||
          r.handler.toLowerCase().includes(q) ||
          r.method.toLowerCase().includes(q)
      );
    }
    this.tableDataSource.data = rows;
    this.paginator?.firstPage();
    this.cdr.markForCheck();
  }

  private connectPaginator(): void {
    if (this.paginator) {
      this.tableDataSource.paginator = this.paginator;
      this.cdr.markForCheck();
    }
  }
}
